import React, { useCallback, useMemo, useRef, useState } from "react";
import { PaymentMethodVM, PaymentMethodEntityTypesEnum, PaymentMethodTypesEnum } from "@propertelligent/client-api";
import { GetImages } from "../../../utils/GetImages";
import ActionMenu from "../../../newComponents/actionMenus/action-menu";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import "./PaymentAccountCardItem.css";
import useInfiniteScroll from "react-infinite-scroll-hook";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../../newComponents/emptystate";
import dollarIcon from "../../../assets/blueDollarSign.svg";
import apiHelper from "../../../utils/apiHelper";
import { PaymentService } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";
import BluePayableIcon from "../../../assets/Payment-Icon.svg";

interface DataHookState {
  rows: PaymentMethodVM[];
  loading: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  currentPage: number;
}

interface PaymentAccountCardItemProps {
  className?: string;
  dataHookState: DataHookState;
  onEditPaymentMethod?: (paymentMethod: PaymentMethodVM) => void;
  onReconnectPlaid?: (paymentMethod: PaymentMethodVM) => void;
  /** Renames a Plaid-typed row's AccountName without touching its Plaid connection -- distinct from
   * onEditPaymentMethod, which only applies to the legacy ACH/CreditCard edit flow. */
  onRenamePlaidAccount?: (paymentMethod: PaymentMethodVM) => void;
  hasAnyPaymentAccounts?: boolean;
  /** When false, hides Edit action (e.g. for tenant - delete only) */
  showEditAction?: boolean;
  /** When true, skip the org-user "PaymentAccounts" permission check entirely -- for callers (e.g.
   * TenantPaymentMethodTab) where ownership is already the only authorization boundary that applies,
   * and the caller has no org permission map to check in the first place (canWrite would otherwise
   * always evaluate false for a Tenant/Contractor session, hiding Reconnect/Delete permanently).
   * Defaults to false so the org-admin Payment Accounts page (which never passes this prop) is
   * unaffected. */
  alwaysAllowActions?: boolean;
}

const PaymentAccountCardItem = ({
  className = "",
  dataHookState,
  onEditPaymentMethod,
  onReconnectPlaid,
  onRenamePlaidAccount,
  hasAnyPaymentAccounts = true,
  showEditAction = true,
  alwaysAllowActions = false,
}: PaymentAccountCardItemProps) => {
  const { rows, loading, hasMore, fetchData, currentPage } = dataHookState;
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const settingDefaultRef = useRef(false);

  const check = usePermissions();
  const hasWriteAccess = alwaysAllowActions || check.canWrite("PaymentAccounts");

  const handleSetDefaultPaymentMethod = useCallback((paymentMethodId: number | undefined) => {
    if (paymentMethodId == null || settingDefaultRef.current) return;

    settingDefaultRef.current = true;
    apiHelper(PaymentService.putApiPaymentMethodSetDefault, {
      successMessage: "Default Payment Account Updated",
      showNotification: true,
    }, paymentMethodId)
      .then((res) => {
        if (res?.data?.success) {
          fetchData(1);
        }
      })
      .finally(() => {
        settingDefaultRef.current = false;
      });
  }, [fetchData]);

  // Define menu items for each row using useMemo outside the loop
  const menuItemsMap = useMemo(() => {
    return rows.reduce((acc, listingData) => {
      const items = [];
      const isPlaid = listingData.paymentMethodTypeId === PaymentMethodTypesEnum.Plaid;
      const isPlaidConnected = listingData.plaidPaymentMethod?.isConnected === true;

      if (isPlaid) {
        // Plaid: renaming the account name is always available (does not require re-linking); Reconnect
        // only when isConnected is false.
        if (onRenamePlaidAccount) {
          items.push({
            icon: editIcon,
            label: "Edit",
            action: () => onRenamePlaidAccount(listingData),
            isDivider: false,
            isDestructive: false,
          });
          items.push({ isDivider: true });
        }
        if (!isPlaidConnected) {
          items.push({
            icon: GetImages.LinkIcon,
            label: "Reconnect",
            action: () => onReconnectPlaid?.(listingData),
            isDivider: false,
            isDestructive: false,
          });
          items.push({ isDivider: true });
        }
        if (
          isPlaidConnected &&
          !listingData.isDefault &&
          hasWriteAccess &&
          listingData.entityTypeId === PaymentMethodEntityTypesEnum.Organization
        ) {
          items.push({
            icon: GetImages.FilledBlueStar,
            label: "Set as Default",
            action: () => handleSetDefaultPaymentMethod(listingData.id),
            isDivider: false,
            isDestructive: false,
          });
          items.push({ isDivider: true });
        }
      } else {
        // ACH, Credit Card: Edit (if showEditAction)
        if (showEditAction) {
          items.push(
            {
              icon: editIcon,
              label: "Edit",
              action: () => onEditPaymentMethod?.(listingData),
              isDivider: false,
              isDestructive: false,
            },
            { isDivider: true }
          );
        }
      }

      if (hasWriteAccess) {
        items.push({
          icon: deleteIcon,
          label: "Delete",
          action: () => setShowConfirmation(String(listingData.id)),
          isDivider: false,
          isDestructive: true,
        });
      }

      acc[listingData.id || 0] = items;
      return acc;
    }, {} as Record<string | number, any[]>);
  }, [rows, onEditPaymentMethod, onReconnectPlaid, onRenamePlaidAccount, showEditAction, hasWriteAccess, handleSetDefaultPaymentMethod]);

  const loadMoreRecords = () => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  const handleDeletePaymentMethod = (paymentMethodId: any) => {
    if (isDeleting) return;

    setIsDeleting(true);
    apiHelper(PaymentService.deleteApiPaymentMethod, {
      successMessage: "Payment Method Deleted Successfully",
      showNotification: true,
    }, paymentMethodId)
      .then((res) => {
        if (res?.data?.success) {
          fetchData(1);
          setShowConfirmation(null);
        }
      })
      .catch((err) => {
        console.error("Error deleting payment method:", err);
        setShowConfirmation(null);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <div className="infinite-scroll-container">
      {loading && rows.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : rows.length === 0 && !loading ? (
        <EmptyState
          icon={BluePayableIcon}
          heading={hasAnyPaymentAccounts ? "No Payment Accounts Found" : "Add Your First Payment Account"}
          buttonLabel={hasAnyPaymentAccounts ? "Add Your Payment Account" : "Add Payment Account"}
          isButton={hasAnyPaymentAccounts ? false : true}
          onButtonClick={() => onEditPaymentMethod?.({} as PaymentMethodVM)}
        />
      ) : (
        <>
          {rows.map((listingData) => (
            <div key={listingData.id} className={`listing-card ${className}`}>
              <div className="listing-card-header">
                <div className="listing-image-container payment-account-box">
                  <img
                    src={GetImages.paymentAccountIcon}
                    alt="Payment Method"
                    width={32}
                    height={32}
                    className="listing-image color-white-icon"
                  />
                </div>
                <div className="listing-details">
                  <div className="listing-info-container">
                    <div className="listing-info">
                      <h3 className="listing-address">{listingData.accountName}</h3>
                      <p className="listing-type">
                        {listingData?.formatted?.PaymentMethodTypeId} •{" "}
                        {listingData?.paymentMethodTypeId === PaymentMethodTypesEnum.CreditCard
                          ? "XXXXXXXX" + listingData?.creditCardPaymentMethod?.cardNumber?.slice(8)
                          : listingData?.paymentMethodTypeId === PaymentMethodTypesEnum.ACH
                          ? "XXXXXXXX" + listingData?.achPaymentMethod?.accountNumber?.slice(8)
                          : listingData?.paymentMethodTypeId === PaymentMethodTypesEnum.Plaid
    ? "****" + (listingData?.plaidPaymentMethod?.accountNumberMask || "")
                          : ""}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="payment-account-card-actions">
                  {listingData.isDefault && (
                    <span className="payment-account-default-badge">Default</span>
                  )}
                  {listingData.paymentMethodTypeId === PaymentMethodTypesEnum.Plaid &&
                    listingData.plaidPaymentMethod?.isConnected === true && (
                      <span className="payment-account-connected-badge">Connected</span>
                    )}
                  {hasWriteAccess && menuItemsMap[listingData.id || 0]?.length > 0 && (
                    <ActionMenu
                      trigger={<img src={dropdownIcon} alt="Menu" width={24} height={24} className="iconStyleWithWhiteFilter" />}
                      menuItems={menuItemsMap[listingData.id || 0]}
                      Id={listingData.id || 0}
                    />
                  )}
                </div>
              </div>

              {showConfirmation === String(listingData.id) && (
                <ConfirmationPopup
                  isOpen={true}
                  title="Delete Payment Method"
                  message="Do you want to delete this payment method?"
                  primaryButtonLabel="Delete"
                  secondaryButtonLabel="Cancel"
                  onPrimaryAction={() => handleDeletePaymentMethod(listingData.id)}
                  onSecondaryAction={() => setShowConfirmation(null)}
                  onClose={() => setShowConfirmation(null)}
                  isPrimaryDestructive={true}
                  isLoading={isDeleting}
                  loadingLabel="Deleting..."
                />
              )}
            </div>
          ))}
          {(loading || hasMore) && (
            <div ref={sentryRef} className="loading-trigger">
              {loading && (
                <SimpleSpinner myClassName="Icon40" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentAccountCardItem;